import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 10;

function parsePagination(searchParams: URLSearchParams): PaginationState {
  return {
    pageIndex: Math.max(0, parseInt(searchParams.get('page') ?? '1', 10) - 1),
    pageSize: parseInt(searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE), 10),
  };
}

function parseSorting(searchParams: URLSearchParams): SortingState {
  const sortParam = searchParams.get('sort');
  if (!sortParam) return [];
  return sortParam
    .split(',')
    .map((s) => {
      const colonIndex = s.lastIndexOf(':');
      if (colonIndex === -1) return null;
      const id = s.slice(0, colonIndex);
      const dir = s.slice(colonIndex + 1);
      return id ? { id, desc: dir === 'desc' } : null;
    })
    .filter((s): s is NonNullable<typeof s> => s !== null);
}

function parseFilters(searchParams: URLSearchParams): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  searchParams.forEach((value, key) => {
    if (key.startsWith('f_')) {
      filters.push({ id: key.slice(2), value });
    }
  });
  return filters;
}

function buildParams(pagination: PaginationState, sorting: SortingState, columnFilters: ColumnFiltersState): URLSearchParams {
  const params = new URLSearchParams();
  if (pagination.pageIndex > 0) {
    params.set('page', String(pagination.pageIndex + 1));
  }
  if (pagination.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('pageSize', String(pagination.pageSize));
  }
  if (sorting.length > 0) {
    params.set('sort', sorting.map((s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`).join(','));
  }
  columnFilters.forEach(({ id, value }) => {
    params.set(`f_${id}`, String(value));
  });
  return params;
}

/**
 * Persists table pagination, sorting, and column filter state in URL search params.
 *
 * Uses local useState as the source of truth (stable setters — no debounce issues).
 * A one-way useEffect mirrors state into the URL with replace:true so intra-list
 * navigation doesn't pollute history. Navigating back from a detail page restores
 * the exact list state the user left.
 */
export function useTableUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Seed from URL on mount so back-button restores state
  const [pagination, setPagination] = useState<PaginationState>(() => parsePagination(searchParams));
  const [sorting, setSorting] = useState<SortingState>(() => parseSorting(searchParams));
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => parseFilters(searchParams));

  // One-way sync: state → URL.
  // replace:true keeps history clean. Because we only write when local state
  // changes (not when searchParams changes), there is no feedback loop.
  useEffect(() => {
    setSearchParams(buildParams(pagination, sorting, columnFilters), { replace: true });
  }, [pagination, sorting, columnFilters, setSearchParams]);

  return { pagination, setPagination, sorting, setSorting, columnFilters, setColumnFilters };
}
